import { assistantPanelKey, assistantThreadKey, type TypedStorage } from "@baton/core";
import { create } from "zustand";

/**
 * The panel's state (015 `contracts/screens.md` § 019 Assistant): open or closed and its width, kept in
 * this browser and never in the address, the thread it holds in each Team, so the conversation survives
 * a reload and a switch of Team opens that Team's own, and which of its two views is up, the thread or
 * the list. `hydrate` reads the stored state before the shell's first paint, so a reload opens the panel
 * as it was with nothing flashing; every change writes back. What a page asks the panel to open with
 * is `requests.ts`, which only the pages and the panel's chunk carry, not the shell's entry.
 */
export const PANEL_DEFAULT_WIDTH = 400;
export const PANEL_MIN_WIDTH = 360;
export const PANEL_MAX_WIDTH = 560;

export type PanelView = "thread" | "list" | "summary";

export interface AssistantPanelState {
  readonly open: boolean;
  readonly width: number;
  readonly view: PanelView;
  /** The thread each Team's panel holds, by slug; null for a thread not yet begun. */
  readonly threads: Readonly<Record<string, string>>;
  /** The element that opened the panel, which takes focus back when it closes (§ Focus); not persisted. */
  readonly opener: HTMLElement | null;
  readonly storage: TypedStorage | null;
  readonly hydrate: (storage: TypedStorage) => void;
  readonly setOpen: (open: boolean) => void;
  readonly toggle: () => void;
  readonly setWidth: (width: number) => void;
  readonly setView: (view: PanelView) => void;
  readonly setThread: (ws: string, id: string | null) => void;
}

export function clampPanelWidth(width: number): number {
  return Math.min(PANEL_MAX_WIDTH, Math.max(PANEL_MIN_WIDTH, Math.round(width)));
}

export const useAssistantPanel = create<AssistantPanelState>((set, get) => {
  const persist = (open: boolean, width: number) => {
    get().storage?.set(assistantPanelKey, { open, width });
  };
  return {
    open: false,
    width: PANEL_DEFAULT_WIDTH,
    view: "thread",
    threads: {},
    opener: null,
    storage: null,
    hydrate: (storage) => {
      if (get().storage === storage) return;
      const panel = storage.get(assistantPanelKey);
      set({ storage, open: panel?.open ?? false, width: panel?.width ?? PANEL_DEFAULT_WIDTH, threads: storage.get(assistantThreadKey) ?? {} });
    },
    setOpen: (open) => {
      const active = document.activeElement;
      const opener = open ? (active instanceof HTMLElement ? active : null) : null;
      const previous = get().opener;
      set({ open, view: "thread", opener });
      persist(open, get().width);
      if (!open) {
        // Focus returns to what opened the panel: the rail's tile, or the element the shortcut was pressed on.
        const target = previous?.isConnected === true ? previous : document.querySelector<HTMLElement>("[data-assistant-tile]");
        target?.focus();
      }
    },
    toggle: () => {
      get().setOpen(!get().open);
    },
    setWidth: (width) => {
      const clamped = clampPanelWidth(width);
      set({ width: clamped });
      persist(get().open, clamped);
    },
    setView: (view) => {
      set({ view });
    },
    setThread: (ws, id) => {
      const threads = Object.fromEntries(Object.entries(get().threads).filter(([slug]) => slug !== ws));
      if (id !== null) threads[ws] = id;
      set({ threads, view: "thread" });
      get().storage?.set(assistantThreadKey, threads);
    },
  };
});
